<template>
	<div
		class="teacher-cards-skeleton"
		:class="`teacher-cards-skeleton--${variant}`"
		aria-hidden="true"
	>
		<header v-if="showTitle || showFilters" class="teacher-cards-skeleton__header">
			<span v-if="showTitle" class="teacher-cards-skeleton__title"></span>
			<div v-if="showFilters" class="teacher-cards-skeleton__filters">
				<span v-for="n in 2" :key="n" class="teacher-cards-skeleton__filter"></span>
			</div>
		</header>

		<div class="teacher-cards-skeleton__list">
			<div v-for="n in cardCount" :key="n" class="teacher-cards-skeleton__card">
				<span class="teacher-cards-skeleton__media"></span>
				<div class="teacher-cards-skeleton__content">
					<span class="teacher-cards-skeleton__line teacher-cards-skeleton__line--name"></span>
					<span class="teacher-cards-skeleton__line teacher-cards-skeleton__line--sub"></span>
					<span class="teacher-cards-skeleton__line teacher-cards-skeleton__line--meta"></span>
					<span class="teacher-cards-skeleton__btn"></span>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
	/** carousel：横向资源区；grid：四列推荐区 */
	variant: {
		type: String,
		default: "grid",
		validator: value => ["carousel", "grid"].includes(value),
	},
	count: {
		type: Number,
		default: undefined,
	},
	showTitle: {
		type: Boolean,
		default: true,
	},
	showFilters: {
		type: Boolean,
		default: false,
	},
});

const cardCount = computed(() => {
	if (typeof props.count === "number" && props.count > 0) return props.count;
	return props.variant === "carousel" ? 4 : 8;
});
</script>

<style lang="scss" scoped>
.teacher-cards-skeleton {
	width: 100%;
	min-width: 0;

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		margin-bottom: 30px;
	}

	&__title,
	&__filter,
	&__media,
	&__line,
	&__btn {
		display: block;
		background: linear-gradient(90deg, #f2f2f2 25%, #fafafa 40%, #f2f2f2 60%);
		background-size: 300% 100%;
		animation: teacher-cards-skeleton-shimmer 1.4s ease-in-out infinite;
		border-radius: 8px;
	}

	&__title {
		width: 220px;
		height: 36px;
	}

	&__filters {
		display: flex;
		align-items: center;
		gap: 16px;
	}

	&__filter {
		width: 120px;
		height: 36px;
		border-radius: 8px;
	}

	&__list {
		display: grid;
		gap: 20px;
	}

	&--carousel &__list {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}

	&--grid {
		padding: 30px;
		border-radius: 20px;
		box-shadow: 0 4px 24.6px 0 rgba(0, 0, 0, 0.1);
		box-sizing: border-box;

		.teacher-cards-skeleton__list {
			grid-template-columns: repeat(4, minmax(0, 1fr));
			gap: 24px 20px;
		}
	}

	&__card {
		min-width: 0;
		overflow: hidden;
	}

	&__media {
		width: 100%;
		height: 242px;
		border-radius: 16px 16px 0 0;
	}

	&--carousel &__media {
		height: 248px;
		border-radius: 12px 12px 0 0;
	}

	&__content {
		padding: 16px 14px 18px;
		border: 1px solid #efefef;
		border-top: none;
		border-radius: 0 0 16px 16px;
		background: #ffffff;
		box-sizing: border-box;
	}

	&__line {
		width: 100%;
		height: 16px;
		margin-top: 12px;

		&--name {
			width: 58%;
			height: 22px;
			margin-top: 0;
		}

		&--sub {
			width: 78%;
			height: 14px;
		}

		&--meta {
			width: 46%;
			height: 14px;
		}
	}

	&__btn {
		width: 100%;
		height: 40px;
		margin-top: 16px;
		border-radius: 999px;
	}
}

@keyframes teacher-cards-skeleton-shimmer {
	0% {
		background-position: 100% 0;
	}
	100% {
		background-position: 0 0;
	}
}

@media (max-width: 1200px) {
	.teacher-cards-skeleton {
		&--carousel &__list,
		&--grid &__list {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}
}

@media (max-width: 900px) {
	.teacher-cards-skeleton {
		&--carousel &__list,
		&--grid &__list {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		&__header {
			flex-direction: column;
			align-items: flex-start;
		}
	}
}

@media (max-width: 600px) {
	.teacher-cards-skeleton {
		&--carousel &__list,
		&--grid &__list {
			grid-template-columns: minmax(0, 1fr);
		}
	}
}

@media (prefers-reduced-motion: reduce) {
	.teacher-cards-skeleton__title,
	.teacher-cards-skeleton__filter,
	.teacher-cards-skeleton__media,
	.teacher-cards-skeleton__line,
	.teacher-cards-skeleton__btn {
		animation: none;
	}
}
</style>
